import { createElement } from 'react';

import i18n from '@/core/i18n';
import {
  ChangedFieldsStrategy,
  GenericChangedField,
} from '@/modules/common/components/changed-fields';
import { RateMask } from '@/modules/common/utils/rate-mask';

import { ProjectChangedFieldNameDTO } from '../../types/dto/project-changed-field.dto';
import { ProjectChangedField } from '../../types/project-changes';
import { getLocalizedFieldName } from '../../utils/project-changes/project-change-field-name-mapper';
import {
  formatFieldValue,
  formatValueWithCurrency,
} from '../../utils/project-changes/project-change-field-value-formatter';
import { GroupedFieldItem } from './grouped-field-item';
import { IndustriesValue } from './industries-value';

/**
 * Resolves the currency code for BUDGET from the sibling DEFAULT_CURRENCY_CODE field.
 */
const resolveBudgetCurrency = (allFields: GenericChangedField[]): string | null => {
  const currencyField = allFields.find(
    f => f.field === ProjectChangedFieldNameDTO.DEFAULT_CURRENCY_CODE
  );
  return currencyField?.to || currencyField?.from || null;
};

/**
 * Builds the project-domain strategy for the generic ChangedFieldsCell.
 *
 * Carries project-only concerns: BUDGET currency, FLAT_RATE/USER_CUSTOM_RATE
 * rate masking, INDUSTRIES "+N more" tooltip, and the bespoke USER_RATES grouped
 * renderer (incl. treating a single USER_RATES entry as a one-item group).
 *
 * Built lazily (at render) so i18n strings resolve after resources load.
 */
export const createProjectChangedFieldsStrategy = (): ChangedFieldsStrategy => {
  // Namespace-bound t that mirrors useTranslation('authenticated.projects'):
  // relative keys resolve in-namespace, while explicit "ns:key" keys still work.
  const t = i18n.getFixedT(null, 'authenticated.projects');

  return {
    getLocalizedFieldName: field => getLocalizedFieldName(field as ProjectChangedFieldNameDTO),

    formatFieldValue: (value, field) => formatFieldValue(value, t, field),

    renderValue: (formatted, field, allFields) => {
      if (field === ProjectChangedFieldNameDTO.INDUSTRIES) {
        return createElement(IndustriesValue, { value: formatted });
      }

      const shouldApplyMask = (
        [
          ProjectChangedFieldNameDTO.FLAT_RATE,
          ProjectChangedFieldNameDTO.USER_CUSTOM_RATE,
        ] as string[]
      ).includes(field);
      const maskedValue = shouldApplyMask ? RateMask.applyToString(formatted) : formatted;

      if (field === ProjectChangedFieldNameDTO.BUDGET) {
        const currency = resolveBudgetCurrency(allFields);
        return currency ? formatValueWithCurrency(maskedValue, currency) : maskedValue;
      }

      return maskedValue;
    },

    // USER_RATES are always rendered with the bespoke grouped layout, including a
    // single entry (wrapped as a one-item group below).
    isGroupedField: field =>
      field.field === ProjectChangedFieldNameDTO.USER_RATES && !!field.subfieldChanges,

    renderGrouped: ({ field, allFields }) => {
      const projectField = field as ProjectChangedField;
      const projectAllFields = allFields as ProjectChangedField[];

      // A single (non-grouped) USER_RATES entry is displayed as a one-item group.
      const groupedField: ProjectChangedField = projectField.isGrouped
        ? projectField
        : { ...projectField, isGrouped: true, groupCount: 1, subfieldChanges: [projectField] };

      return createElement(GroupedFieldItem, { field: groupedField, allFields: projectAllFields });
    },

    getEmptyStateMessage: () => t('details.tabs.changes.noChanges'),

    strings: {
      arrow: t('details.tabs.changes.valueDisplay.arrow'),
      changesCount: count => t('details.tabs.changes.changesCount', { count }),
    },
  };
};
